/**
 * RudderStack Analytics Singleton Client
 * 
 * Ensures a single RudderAnalytics instance is created and shared across the app.
 * Prevents multiple .load() calls and inconsistent delivery.
 */

import { RudderAnalytics } from '@rudderstack/analytics-js'
import { debugLog, errorLog } from '../utils/log-helper'

// Module-level singleton state
let client: RudderAnalytics | null = null
let ready = false
let readyCallbacks: Array<() => void> = []
let initPromise: Promise<void> | null = null

/**
 * Initialize RudderStack analytics (idempotent - only runs once)
 * 
 * @returns Object with analytics instance and ready promise
 */
export function initRudder(): {
  analytics: RudderAnalytics | null
  readyPromise: Promise<void>
} {
  // If already initialized, return existing instance
  if (client) {
    if (ready) {
      return { analytics: client, readyPromise: Promise.resolve() }
    }
    // If not ready yet, return the existing init promise
    return {
      analytics: client,
      readyPromise: initPromise ?? Promise.resolve(),
    }
  }

  // Create new instance
  client = new RudderAnalytics()
  
  // Create init promise
  initPromise = new Promise<void>((resolve) => {
    const writeKey = process.env.REACT_APP_RUDDERSTACK_SOURCE ?? ''
    const dataplaneUrl = 'https://okaoincoomexbi.dataplane.rudderstack.com'

    if (!writeKey) {
      errorLog('[RudderClient] REACT_APP_RUDDERSTACK_SOURCE is not set')
      ready = true
      resolve()
      return
    }

    debugLog('[RudderClient] Initializing RudderStack analytics...')
    
    client?.load(writeKey, dataplaneUrl)

    client?.ready(() => {
      debugLog('[RudderClient] Analytics instance is ready!')
      ready = true
      
      // Notify all waiting callbacks
      readyCallbacks.forEach((callback) => {
        try {
          callback()
        } catch (error) {
          errorLog('[RudderClient] Error in ready callback', error)
        }
      })
      readyCallbacks = []
      
      resolve()
    })
  })

  return {
    analytics: client,
    readyPromise: initPromise,
  }
}

/**
 * Get the RudderAnalytics instance (returns null if not initialized)
 */
export function getRudder(): RudderAnalytics | null {
  return client
}

/**
 * Check if RudderStack is ready
 */
export function isRudderReady(): boolean {
  return ready && client !== null
}

/**
 * Subscribe to ready state (calls callback immediately if already ready)
 */
export function onRudderReady(callback: () => void): void {
  if (ready && client) {
    callback()
  } else {
    readyCallbacks.push(callback)
  }
}

/**
 * Reset the singleton (for testing only)
 */
export function resetRudderClient(): void {
  client = null
  ready = false
  readyCallbacks = []
  initPromise = null
}

